<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

// 导入背景图片
import knowledgeBaseGuideImg from 'assets/images/dashboard/pilot/knowladgebase-guide.png';

const props = defineProps({
  // 当前选中的项目
  activeItem: {
    type: String,
    default: 'knowledgeBases',
  },
  // 是否折叠
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'toggle-collapse']);

const { t } = useI18n();

// hover 状态
const hoverKey = ref(null);

// 每个导航的颜色
const navColors = {
  knowledgeBases: '#4B52FA',
  uploadDocument: '#14A2B5',
  crawlWechat: '#2DD4BF',
  syncMarketingCloud: '#EAB308',
  crawlWebpage: '#FE6206',
  addFaq: '#992AFF',
  importFaq: '#00C2CB',
};

// 导航菜单项
const menuItems = computed(() => [
  {
    id: 'logo',
    type: 'logo',
  },
  {
    id: 'knowledgeBases',
    icon: 'i-lucide-book-open',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.MY_KNOWLEDGE_BASES'),
  },
  {
    id: 'sectionAddKnowledge',
    type: 'sectionHeader',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.ADD_KNOWLEDGE'),
  },
  {
    id: 'uploadDocument',
    icon: 'i-ri-file-word-2-line',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.UPLOAD_DOCUMENT'),
    action: 'uploadDocument',
  },
  {
    id: 'crawlWechat',
    icon: 'i-ri-wechat-2-line',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.CRAWL_WECHAT'),
    action: 'crawlWechat',
    developmentToast: true,
  },
  {
    id: 'syncMarketingCloud',
    icon: 'i-lucide-cloud-check',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.SYNC_MARKETING_CLOUD'),
    action: 'syncMarketingCloud',
    developmentToast: true,
  },
  {
    id: 'crawlWebpage',
    icon: 'i-lucide-globe',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.CRAWL_WEBPAGE'),
    action: 'crawlWebpage',
  },
  {
    id: 'sectionAddFaqKnowledge',
    type: 'sectionHeader',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.ADD_FAQ_KNOWLEDGE'),
  },
  {
    id: 'addFaq',
    icon: 'i-lucide-message-circle-question',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.ADD_FAQ'),
    action: 'addFaq',
  },
  {
    id: 'importFaq',
    icon: 'i-lucide-file-spreadsheet',
    label: t('PILOT.KNOWLEDGE_BASES.SIDEBAR.IMPORT_FAQ'),
    action: 'importFaq',
  },
]);

const handleItemClick = item => {
  if (item.type === 'divider' || item.type === 'sectionHeader' || item.disabled)
    return;

  // 显示开发中提示
  if (item.developmentToast) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.SIDEBAR.COMING_SOON'));
    return;
  }

  emit('select', item);
};

const handleToggle = () => {
  emit('toggle-collapse');
};

const isActive = item => {
  return props.activeItem === item.id;
};

const getItemColor = item => {
  if (item.disabled) {
    return '#9CA3AF';
  }
  // 显示彩色图标
  return navColors[item.id] || '#4B52FA';
};
</script>

<template>
  <nav
    class="flex flex-col h-full bg-[#F7F7F7] dark:bg-n-slate-900 rounded-xl transition-all duration-300"
    :class="collapsed ? 'w-16 px-1' : 'w-44 px-2'"
  >
    <!-- 导航菜单 -->
    <div class="flex-1 py-4 overflow-y-auto">
      <!-- 展开状态：水平布局 -->
      <ul v-if="!collapsed" class="flex flex-col gap-1">
        <li v-for="item in menuItems" :key="item.id">
          <!-- Logo 区域 -->
          <div v-if="item.type === 'logo'" class="pb-2 mb-2">
            <div
              class="w-full bg-cover bg-center rounded-xl bg-no-repeat"
              :style="{
                backgroundImage: `url(${knowledgeBaseGuideImg})`,
                paddingBottom: '75%',
              }"
            />
          </div>

          <!-- 分隔线 -->
          <div
            v-else-if="item.type === 'divider'"
            class="my-2 border-t border-n-slate-4 dark:border-n-slate-7"
          />

          <!-- 分组标题 -->
          <div v-else-if="item.type === 'sectionHeader'" class="px-3 pt-4 pb-2">
            <span
              class="text-[11px] font-medium text-n-slate-9 uppercase tracking-wider"
            >
              {{ item.label }}
            </span>
          </div>

          <!-- 菜单项 -->
          <button
            v-else
            type="button"
            class="w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all group relative"
            :class="[
              item.disabled ? 'cursor-not-allowed' : '',
              isActive(item) || hoverKey === item.id
                ? 'bg-white dark:bg-n-slate-800 shadow-sm'
                : '',
            ]"
            @click="handleItemClick(item)"
            @mouseenter="hoverKey = item.id"
            @mouseleave="hoverKey = null"
          >
            <!-- 禁用时的 tooltip -->
            <div
              v-if="item.disabled && hoverKey === item.id"
              class="absolute left-full ml-2 z-50 px-2 py-1 text-xs text-white bg-[#374151] rounded whitespace-nowrap shadow-lg"
            >
              {{ item.disabledTip || '即将到来' }}
              <div
                class="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-[#374151]"
              />
            </div>

            <!-- 图标 -->
            <span
              class="text-[16px] flex-shrink-0 transition-colors"
              :class="[item.icon, { 'opacity-40': item.disabled }]"
              :style="{ color: getItemColor(item) }"
            />

            <!-- 文字 -->
            <span
              class="text-[13px] font-medium truncate transition-colors"
              :class="[
                item.disabled
                  ? 'text-n-slate-9'
                  : isActive(item)
                    ? 'text-n-slate-12 dark:text-white'
                    : 'text-n-slate-11',
              ]"
            >
              {{ item.label }}
            </span>
          </button>
        </li>
      </ul>

      <!-- 收起状态：垂直布局 -->
      <ul v-else class="flex flex-col items-center gap-3">
        <li v-for="item in menuItems" :key="item.id">
          <!-- Logo 区域（收起状态时跳过） -->
          <div v-if="item.type === 'logo'" />

          <!-- 分隔线 -->
          <div
            v-else-if="item.type === 'divider'"
            class="w-10 my-1 border-t border-n-slate-4 dark:border-n-slate-7"
          />

          <!-- 分组标题（收起时显示省略号） -->
          <div v-else-if="item.type === 'sectionHeader'" class="py-2">
            <span class="text-n-slate-9 text-[10px]">•••</span>
          </div>

          <!-- 菜单项 -->
          <button
            v-else
            type="button"
            class="flex flex-col items-center gap-1 relative group"
            :class="{ 'cursor-not-allowed': item.disabled }"
            @click="handleItemClick(item)"
            @mouseenter="hoverKey = item.id"
            @mouseleave="hoverKey = null"
          >
            <!-- 折叠时的 tooltip -->
            <div
              class="absolute left-full ml-2 z-50 px-2 py-1 text-xs text-white bg-[#374151] rounded whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            >
              <span :class="{ 'text-n-slate-4': item.disabled }">{{
                item.label
              }}</span>
              <span v-if="item.disabled"
class="ml-1 text-n-amber-4"
                >({{ item.disabledTip }})</span>
              <div
                class="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-[#374151]"
              />
            </div>

            <!-- 图标容器 -->
            <div
              class="p-[5px] rounded-lg transition-all"
              :class="[
                item.disabled
                  ? ''
                  : isActive(item) || hoverKey === item.id
                    ? 'shadow-[0_2px_8px_rgba(0,0,0,0.15)] bg-white dark:bg-n-slate-800'
                    : '',
              ]"
            >
              <span
                class="text-[16px] block transition-colors"
                :class="[item.icon, { 'opacity-40': item.disabled }]"
                :style="{ color: getItemColor(item) }"
              />
            </div>

            <!-- 名称（2个字截断） -->
            <span
              class="text-[10px] font-medium w-full text-center leading-tight overflow-hidden"
              :class="[
                item.disabled
                  ? 'text-n-slate-9'
                  : isActive(item)
                    ? 'text-n-slate-12 dark:text-white'
                    : 'text-n-slate-11',
              ]"
              style="
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
              "
            >
              {{ item.label }}
            </span>
          </button>
        </li>
      </ul>
    </div>

    <!-- 底部折叠按钮 -->
    <div class="py-3 border-t border-n-slate-4 dark:border-n-slate-7">
      <button
        type="button"
        class="w-full flex items-center justify-center gap-2 py-1.5 text-n-slate-10 hover:text-n-slate-12 transition-colors"
        @click="handleToggle"
      >
        <span
          :class="
            collapsed ? 'i-lucide-panel-left-close' : 'i-lucide-panel-left'
          "
          class="text-lg"
        />
        <span v-if="!collapsed" class="text-xs">
          {{ t('PILOT.KNOWLEDGE_BASES.SIDEBAR.COLLAPSE') }}
        </span>
      </button>
    </div>
  </nav>
</template>
